import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { CustomerGroup, CreateGroupPayload, UpdateGroupPayload } from '../../../../models/customer-group';
import { CustomerGroupsService } from '../../../../services/customer-groups/customer-groups-service';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BADGE_BYTES = 20 * 1024 * 1024;

@Component({
  selector: 'app-add-group-form',
  imports: [ReactiveFormsModule, Dialog, ButtonModule, InputTextModule, TextareaModule],
  templateUrl: './add-group-form.html',
  styleUrl: './add-group-form.scss',
})
export class AddGroupForm {
  private groupsService = inject(CustomerGroupsService);
  private fb = inject(FormBuilder);

  @Output() visibilityChange = new EventEmitter<boolean>();
  @Output() saved   = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();

  @Input() editGroup: CustomerGroup | null = null;

  @Input()
  get visibility() { return this._visibility; }
  set visibility(v: boolean) { this._visibility = v; if (v) this.reset(); }
  private _visibility = false;

  form: FormGroup = this.fb.group({
    name:        ['', Validators.required],
    description: [''],
    color:       ['#2563eb', Validators.required],
    status:      ['ACTIVE'],
  });

  loading = false;
  isSubmitted = false;
  nameError = '';

  // Badge state
  badgeFile: File | null = null;
  badgePreview: string | null = null;
  badgeError = '';
  badgeLoading = false;
  currentBadgeUrl: string | null = null;
  badgeDeletePending = false;

  get isEditMode() { return !!this.editGroup; }

  submit() {
    this.isSubmitted = true;
    this.nameError = '';
    if (this.form.invalid) return;
    this.loading = true;

    const req$ = this.isEditMode
      ? this.groupsService.updateGroup(this.editGroup!.id, this.form.value as UpdateGroupPayload)
      : this.groupsService.createGroup(this.form.value as CreateGroupPayload);

    req$.pipe(finalize(() => { this.loading = false; })).subscribe({
      next: (res) => {
        const groupId = this.isEditMode ? this.editGroup!.id : res.data.id;
        const done = () => { if (this.isEditMode) { this.saved.emit(); } else { this.created.emit(); } this.close(); };
        if (this.badgeDeletePending && this.isEditMode) {
          this.badgeLoading = true;
          this.groupsService.deleteBadge(groupId)
            .pipe(finalize(() => { this.badgeLoading = false; }))
            .subscribe({ next: () => {
              if (this.badgeFile) { this.uploadBadge(groupId, done); } else { done(); }
            }, error: () => done() });
        } else if (this.badgeFile) {
          this.uploadBadge(groupId, done);
        } else {
          done();
        }
      },
      error: (err) => {
        const status = err?.status;
        const msg: string = err?.error?.message ?? '';
        if (status === 409) {
          this.nameError = msg || 'A group with this name already exists.';
        } else if (status === 400) {
          this.nameError = msg || 'Invalid request.';
        }
      },
    });
  }

  onBadgeFileChange(event: Event) {
    this.badgeError = '';
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      this.badgeError = 'Allowed types: jpg, png, gif, webp.';
      return;
    }
    if (file.size > MAX_BADGE_BYTES) {
      this.badgeError = 'File must be under 20 MB.';
      return;
    }
    this.badgeFile = file;
    const reader = new FileReader();
    reader.onload = (e) => { this.badgePreview = e.target?.result as string; };
    reader.readAsDataURL(file);
  }

  removeBadgeSelection() {
    this.badgeFile = null;
    this.badgePreview = null;
    this.badgeError = '';
  }

  deleteBadge() {
    this.currentBadgeUrl = null;
    this.badgeDeletePending = true;
  }

  private uploadBadge(groupId: number, onDone: () => void) {
    this.badgeLoading = true;
    this.groupsService.uploadBadge(groupId, this.badgeFile!)
      .pipe(finalize(() => { this.badgeLoading = false; }))
      .subscribe({
        next: () => { onDone(); },
        error: (err) => {
          const msg: string = err?.error?.message ?? '';
          this.badgeError = msg || 'Badge upload failed.';
          onDone();
        },
      });
  }

  cancel() { this.close(); }

  private close() {
    this._visibility = false;
    this.visibilityChange.emit(false);
  }

  private reset() {
    this.isSubmitted = false;
    this.loading = false;
    this.nameError = '';
    this.badgeFile = null;
    this.badgePreview = null;
    this.badgeError = '';
    this.badgeLoading = false;
    this.badgeDeletePending = false;
    this.currentBadgeUrl = this.editGroup?.badgeUrl ?? null;
    this.form.reset({
      name:        this.editGroup?.name        ?? '',
      description: this.editGroup?.description ?? '',
      color:       this.editGroup?.color       ?? '#2563eb',
      status:      this.editGroup?.status      ?? 'ACTIVE',
    });
  }
}
